/* eslint-disable import/no-duplicates */
import React, { useState } from 'react';
import { addDays, format, isToday, startOfDay, subDays } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Chart } from 'react-google-charts';
import { useCurrentEffect } from '../../../../../../../../lib/use-current-effect';

import {
  getLastMeasurementsInfo,
  getMetricValuesByRange,
} from '../../../../../../../../services/metrics';
import { GraphData } from '../../../../../../../../entities/GraphData';
import { usePatient } from '../../../../../../../../hooks/patient';
import Img from '../../../../../../../../assets/images/calorias.svg';

import Default, { ShowScreenType } from '../_Default';
import { ChartsProps } from '../../MetricView';
import { DetailsChart } from '../../MetricView/styles';
import { MetricTypeEnum } from '../../../../../../../../enums/MetricTypeEnum';

/**
 * O total de calorias de cada dia, em ordem: o MAIOR valor do dia, a mesma regra do
 * `GET /metrics/summary`.
 *
 * ⚠️ **Não é a última leitura do dia.** As calorias vêm dentro do item `steps`, e só a
 * pulseira as manda: quando a última leitura de passos do dia é do Health Connect, ela
 * vem sem calorias. Com o `groupByDay` (que devolve só a última), esse dia inteiro
 * sumia do gráfico — medido na API local em 01/10, o dia de 350 kcal que o quadro ao
 * lado mostrava como máximo.
 */
const dailyCalories = (
  items: GraphData[],
): { day: Date; calories: number }[] => {
  const byDay = new Map<string, { day: Date; calories: number }>();

  items.forEach((item) => {
    if (typeof item.calories !== 'number') return;

    const day = startOfDay(new Date(item.mensuramentDate));
    const key = format(day, 'yyyy-MM-dd');
    const current = byDay.get(key);

    if (!current || item.calories > current.calories) {
      byDay.set(key, { day, calories: item.calories });
    }
  });

  return [...byDay.values()].sort((a, b) => a.day.getTime() - b.day.getTime());
};

const CalorieChart: React.FC<ChartsProps> = ({ dateRange }) => {
  const { patient } = usePatient();

  const [showScreen, setShowScreen] = useState<ShowScreenType>('loading');
  const [dataOneDay, setDataOneDay] = useState<string | number>();
  const [graphData, setGraphData] = useState<(string | number)[][]>([
    ['00:00', 0],
  ]);

  useCurrentEffect(
    (isCurrent) => {
      (() => {
        if (patient?.id) {
          if (isCurrent()) {
            setShowScreen('loading');
          }

          const { startDate, endDate } = dateRange;

          if (startDate === endDate) {
            getLastMeasurementsInfo({
              patientId: patient.id,
              metrics: [MetricTypeEnum.STEPS],
            })
              .then((response) => {
                const checkMeasurementToday = response.find(
                  (item) =>
                    item.metricType === MetricTypeEnum.STEPS &&
                    isToday(new Date(item.mensuramentDate)),
                );

                if (isCurrent()) {
                  if (checkMeasurementToday) {
                    setShowScreen('chart');
                    setDataOneDay(checkMeasurementToday.calories);
                  } else {
                    setShowScreen('noData');
                  }
                }
              })
              .catch(() => {
                if (isCurrent()) {
                  setShowScreen('noData');
                }
              });
          } else {
            // ⚠️ Calorias não têm item próprio no metrics: viajam dentro do item
            // `steps`. Consultar `calories` devolvia sempre vazio. Todas as leituras
            // do período (uma por coleta, ~1/h), reduzidas a um total por dia em
            // `dailyCalories`. O fim vai para o início do dia seguinte: esta rota
            // compara o fim só pela data e deixaria o último dia de fora.
            getMetricValuesByRange({
              patientId: patient.id,
              metricType: MetricTypeEnum.STEPS,
              startDate: startOfDay(startDate),
              endDate: startOfDay(addDays(endDate, 1)),
            })
              .then((stepsResponse) => {
                const days = dailyCalories(stepsResponse);

                if (isCurrent()) {
                  if (days.length > 0) {
                    const result: (string | number)[][] = days.map(
                      ({ day, calories }) => [format(day, "dd'/'MM"), calories],
                    );

                    if (result.length === 1) {
                      const date = days[0].day;
                      const dayOne = format(
                        new Date(subDays(date, 1)),
                        "dd'/'MM'/'yyyy",
                      );
                      const dayTwo = format(
                        new Date(subDays(date, 2)),
                        "dd'/'MM'/'yyyy",
                      );

                      result.unshift([dayOne, 0]);
                      result.unshift([dayTwo, 0]);
                    }

                    setShowScreen('chart');
                    setGraphData(result);
                  } else {
                    setShowScreen('noData');
                  }
                }
              })
              .catch(() => {
                if (isCurrent()) {
                  setShowScreen('noData');
                }
              });
          }
        }
      })();
    },
    [dateRange, patient],
  );

  return (
    <Default showScreen={showScreen}>
      {dateRange.startDate === dateRange.endDate ? (
        <DetailsChart color="#FF9A1F">
          <h2>
            {`Hoje - ${format(dateRange.startDate, "dd'/'MM", {
              locale: ptBR,
            })}`}
          </h2>

          <div>
            <img src={Img} alt="icone de calorias" />

            <div>
              <span>Hoje o paciente gastou</span>

              <h1>{dataOneDay}</h1>

              <span>calorias</span>
            </div>
          </div>
        </DetailsChart>
      ) : (
        <Chart
          width="100%"
          height="300px"
          chartType="Bar"
          options={{
            legend: { position: 'none' },
            colors: ['#30E3E5', '#2D9CDB'],
          }}
          data={[['', 'Calorias'], ...graphData]}
          rootProps={{ 'data-testid': '5' }}
        />
      )}
    </Default>
  );
};

export default CalorieChart;
